import { supabase } from "../../supabase/supabase";
import { PhoneNumber, PhoneNumberFormData } from "../models/PhoneNumber";

export const getPhoneNumbers = async (
  organizationId?: string,
): Promise<PhoneNumber[]> => {
  // Get the user's organization ID
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;

  if (!userId) {
    throw new Error("User not authenticated");
  }

  let query;

  if (organizationId) {
    // If organization ID is provided, use it directly
    query = supabase
      .from("phone_numbers")
      .select("*")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });
  } else {
    // Get the user's organization
    const { data: orgData, error: orgError } = await supabase
      .from("organization_members")
      .select("organization_id")
      .eq("user_id", userId)
      .single();

    if (orgError) {
      console.error("Error fetching organization:", orgError);
      return [];
    }

    query = supabase
      .from("phone_numbers")
      .select("*")
      .eq("organization_id", orgData.organization_id)
      .order("created_at", { ascending: false });
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching phone numbers:", error);
    throw error;
  }

  return data as PhoneNumber[];
};

export const getPhoneNumberById = async (id: string): Promise<PhoneNumber> => {
  const { data, error } = await supabase
    .from("phone_numbers")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error(`Error fetching phone number with id ${id}:`, error);
    throw error;
  }

  return data as PhoneNumber;
};

export const createPhoneNumber = async (
  phoneNumberData: PhoneNumberFormData,
  organizationId?: string,
): Promise<PhoneNumber> => {
  // Get the user's organization ID
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;

  if (!userId) {
    throw new Error("User not authenticated");
  }

  // If organization ID is not provided, get the user's organization
  let orgId = organizationId;
  if (!orgId) {
    const { data: orgData, error: orgError } = await supabase
      .from("organization_members")
      .select("organization_id")
      .eq("user_id", userId)
      .single();

    if (orgError) {
      console.error("Error fetching organization:", orgError);
      throw new Error(
        "Could not determine your organization. Please ensure you belong to an organization.",
      );
    }

    orgId = orgData.organization_id;
  }

  // Normalize the phone number by removing spaces and dashes
  const normalizedData = {
    ...phoneNumberData,
    phone_number: phoneNumberData.phone_number.replace(/[\s-]/g, ""),
    organization_id: orgId,
    created_by: userId,
  };

  // Duplicate check removed - allowing duplicate phone numbers

  const { data, error } = await supabase
    .from("phone_numbers")
    .insert(normalizedData)
    .select()
    .single();

  if (error) {
    console.error("Error creating phone number:", error);
    throw error;
  }

  return data as PhoneNumber;
};

export const updatePhoneNumber = async (
  id: string,
  phoneNumberData: Partial<PhoneNumberFormData>,
): Promise<PhoneNumber> => {
  const { data, error } = await supabase
    .from("phone_numbers")
    .update({
      ...phoneNumberData,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error(`Error updating phone number with id ${id}:`, error);
    throw error;
  }

  return data as PhoneNumber;
};

export const deletePhoneNumber = async (id: string): Promise<void> => {
  const { error } = await supabase.from("phone_numbers").delete().eq("id", id);

  if (error) {
    console.error(`Error deleting phone number with id ${id}:`, error);
    throw error;
  }
};
